/**
 * Workforce hooks using TanStack Query
 * Provides data fetching, caching, and mutations for work contracts,
 * workforce summary, overtime accounts, and vacation balances.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  ApiError,
  WorkContractsService,
  WorkforceService,
  VacationBalancesService,
} from '@/api';
import { toast } from 'sonner';
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import type {
  CreateWorkContractRequest,
  UpdateWorkContractRequest,
  AdjustOvertimeRequest,
  CreateVacationBalanceRequest,
} from '@/types/workforce';
// #1156: these hooks declared hand-written all-required response types while the
// generated services resolve to the all-OPTIONAL wire DTOs, so every `useQuery`
// overload failed and `data` degraded to `unknown` (which is what made
// `WorkforceDashboardPage`'s `data.employees` read a TS2339). The generated types
// are the truth on the wire; consumers narrow the optionals at the read site.
import type { workforce_WorkforceSummaryResponse } from '@/api/models/workforce_WorkforceSummaryResponse';
import type { workforce_WorkContractListResponse } from '@/api/models/workforce_WorkContractListResponse';
import type { workforce_WorkContractResponse } from '@/api/models/workforce_WorkContractResponse';
import type { workforce_OvertimeHistoryResponse } from '@/api/models/workforce_OvertimeHistoryResponse';
import type { workforce_OvertimeAccountResponse } from '@/api/models/workforce_OvertimeAccountResponse';
import type { workforce_VacationBalanceResponse } from '@/api/models/workforce_VacationBalanceResponse';
import type { workforce_VacationSummaryResponse } from '@/api/models/workforce_VacationSummaryResponse';
import type { workforce_AbsenceSummaryResponse } from '@/api/models/workforce_AbsenceSummaryResponse';
import { employeeKeys } from './useEmployees';

// =====================================================
// Query key factory
// =====================================================

export const workforceKeys = {
  all: ['workforce'] as const,
  contracts: () => [...workforceKeys.all, 'contracts'] as const,
  contractList: (employeeId: string) =>
    [...workforceKeys.contracts(), 'list', employeeId] as const,
  contractDetail: (id: string) =>
    [...workforceKeys.contracts(), 'detail', id] as const,
  summary: () => [...workforceKeys.all, 'summary'] as const,
  summaryFiltered: (params: Record<string, string>) =>
    [...workforceKeys.summary(), params] as const,
  overtime: () => [...workforceKeys.all, 'overtime'] as const,
  overtimeHistory: (employeeId: string, fromMonth?: string, toMonth?: string) =>
    [...workforceKeys.overtime(), employeeId, fromMonth, toMonth] as const,
  vacationBalance: (employeeId: string, year: number) =>
    [...workforceKeys.all, 'vacation', employeeId, year] as const,
  vacationSummaries: (employeeId: string) =>
    [...workforceKeys.all, 'vacationSummary', employeeId] as const,
  vacationSummary: (employeeId: string, year: number) =>
    [...workforceKeys.vacationSummaries(employeeId), year] as const,
  absenceSummary: (employeeId: string, year: number) =>
    [...workforceKeys.all, 'absenceSummary', employeeId, year] as const,
};

// =====================================================
// Error handling
// =====================================================

type WorkforceErrorContext =
  | 'contractCreate'
  | 'contractUpdate'
  | 'contractDelete'
  | 'summaryFetch'
  | 'overtimeAdjust'
  | 'vacationBalanceCreate'
  | 'error';

function handleWorkforceError(
  error: Error,
  i18n: I18n,
  context: WorkforceErrorContext
): void {
  const lowerMessage = (error.message || '').toLowerCase();

  if (error instanceof ApiError) {
    if (error.status === 409) {
      if (
        lowerMessage.includes('modified by another user') ||
        lowerMessage.includes('optimistic')
      ) {
        toast.error(
          i18n._({
            id: 'workforce.toast.optimisticLock',
            message:
              'Record was modified by another user. Please refresh.',
          })
        );
        return;
      }
      if (lowerMessage.includes('overlapping')) {
        toast.error(
          i18n._({
            id: 'workforce.toast.overlapping',
            message:
              'Contract dates overlap with an existing contract',
          })
        );
        return;
      }
      toast.error(
        i18n._({
          id: 'workforce.toast.conflict',
          message: 'A conflict occurred. Please try again.',
        })
      );
      return;
    }

    if (error.status === 404) {
      toast.error(
        i18n._({
          id: 'workforce.toast.notFound',
          message: 'Record not found',
        })
      );
      return;
    }
  }

  // Fallback based on context
  switch (context) {
    case 'contractCreate':
      toast.error(
        i18n._({
          id: 'workforce.toast.contractCreateError',
          message: 'Failed to create work contract',
        })
      );
      break;
    case 'contractUpdate':
      toast.error(
        i18n._({
          id: 'workforce.toast.contractUpdateError',
          message: 'Failed to update work contract',
        })
      );
      break;
    case 'contractDelete':
      toast.error(
        i18n._({
          id: 'workforce.toast.contractDeleteError',
          message: 'Failed to delete work contract',
        })
      );
      break;
    case 'overtimeAdjust':
      toast.error(
        i18n._({
          id: 'workforce.toast.overtimeAdjustError',
          message: 'Failed to adjust overtime',
        })
      );
      break;
    case 'vacationBalanceCreate':
      toast.error(
        i18n._({
          id: 'workforce.toast.vacationBalanceCreateError',
          message: 'Failed to create vacation balance',
        })
      );
      break;
    case 'summaryFetch':
    case 'error':
    default:
      toast.error(
        i18n._({
          id: 'workforce.toast.error',
          message: 'Error performing workforce operation',
        })
      );
      break;
  }
}

// =====================================================
// Query hooks
// =====================================================

/**
 * Hook to fetch work contracts for an employee.
 */
export function useWorkContracts(employeeId: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<workforce_WorkContractListResponse>({
    queryKey: workforceKeys.contractList(employeeId),
    queryFn: () => WorkContractsService.getApiV1WorkContracts(employeeId),
    enabled: !!employeeId && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch workforce summary with optional filters.
 */
export function useWorkforceSummary(params: {
  from_date: string;
  to_date: string;
  employee_id?: string;
  granularity?: string;
}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  const queryParams: Record<string, string> = {
    from_date: params.from_date,
    to_date: params.to_date,
  };
  if (params.employee_id) queryParams.employee_id = params.employee_id;
  if (params.granularity) queryParams.granularity = params.granularity;

  return useQuery<workforce_WorkforceSummaryResponse>({
    queryKey: workforceKeys.summaryFiltered(queryParams),
    queryFn: () =>
      WorkforceService.getApiV1WorkforceSummary(
        params.from_date,
        params.to_date,
        params.employee_id,
        params.granularity
      ),
    enabled:
      !!params.from_date &&
      !!params.to_date &&
      isSignedIn &&
      isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch overtime history for an employee.
 * Defaults to the last 12 months if fromMonth/toMonth are not provided.
 */
export function useOvertimeHistory(
  employeeId: string,
  fromMonth?: string,
  toMonth?: string
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  const now = new Date();
  const defaultFrom = `${now.getFullYear() - 1}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const defaultTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return useQuery<workforce_OvertimeHistoryResponse>({
    queryKey: workforceKeys.overtimeHistory(employeeId, fromMonth || defaultFrom, toMonth || defaultTo),
    queryFn: () =>
      WorkforceService.getApiV1WorkforceOvertime(
        employeeId,
        fromMonth || defaultFrom,
        toMonth || defaultTo
      ),
    enabled: !!employeeId && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch vacation balance for an employee in a given year.
 */
export function useVacationBalance(employeeId: string, year: number) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<workforce_VacationBalanceResponse>({
    queryKey: workforceKeys.vacationBalance(employeeId, year),
    queryFn: () =>
      VacationBalancesService.getApiV1VacationBalances(employeeId, year),
    enabled: !!employeeId && year > 0 && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch the derived vacation summary (entitlement/used/remaining,
 * in days and hours) for an employee in a given year.
 */
export function useVacationSummary(employeeId: string, year: number) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<workforce_VacationSummaryResponse>({
    queryKey: workforceKeys.vacationSummary(employeeId, year),
    queryFn: () => WorkforceService.getApiV1WorkforceVacationSummary(employeeId, year),
    enabled: !!employeeId && year > 0 && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      return failureCount < 3;
    },
  });
}

/**
 * An employee's sick and accident days of a year (#1979), the cockpit's rule.
 */
export function useAbsenceSummary(employeeId: string, year: number) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<workforce_AbsenceSummaryResponse>({
    queryKey: workforceKeys.absenceSummary(employeeId, year),
    queryFn: () => WorkforceService.getApiV1WorkforceAbsenceSummary(employeeId, year),
    enabled: !!employeeId && year > 0 && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// =====================================================
// Mutation hooks
// =====================================================

/**
 * invalidateEmployeeStatusDerivation refreshes the employee rows a work-contract
 * write changes the DERIVATION of (issue #1224).
 *
 * `employees.status` is derived from `employee_work_contracts`, and the API
 * reports per row whether the derivation job owns the value (`statusDerived`).
 * Adding the first work contract flips that flag to `true`, and deleting the last
 * one flips it back to `false`. `WorkContractForm` sits INSIDE `EditEmployeeForm`,
 * so without this invalidation the status select keeps the editability the page
 * loaded with: the admin adds a contract, the select stays writable, and the next
 * save is rejected with 422 ENTITY_STATUS_DERIVED.
 *
 * The employee's role, hierarchy level and employment start/end dates are
 * contract-derived too (migration 179), so the same refetch keeps the
 * employee table and detail in step with the contract that was just saved.
 */
function invalidateEmployeeStatusDerivation(
  queryClient: ReturnType<typeof useQueryClient>,
  employeeId: string
) {
  queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
  queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
}

/**
 * Hook to create a work contract.
 */
export function useCreateWorkContract() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<workforce_WorkContractResponse, Error, CreateWorkContractRequest>({
    mutationFn: (request) =>
      WorkContractsService.postApiV1WorkContracts(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workforceKeys.contractList(variables.employee_id),
      });
      queryClient.invalidateQueries({
        queryKey: workforceKeys.summary(),
      });
      invalidateEmployeeStatusDerivation(queryClient, variables.employee_id);
      // The vacation entitlement is derived from the contract's per-year values.
      queryClient.invalidateQueries({
        queryKey: workforceKeys.vacationSummaries(variables.employee_id),
      });
      toast.success(
        i18n._({
          id: 'workforce.toast.contractCreateSuccess',
          message: 'Work contract created successfully',
        })
      );
    },
    onError: (error: Error) =>
      handleWorkforceError(error, i18n, 'contractCreate'),
  });
}

/**
 * Hook to update a work contract.
 */
export function useUpdateWorkContract() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      employeeId: string;
      request: UpdateWorkContractRequest;
    }) => WorkContractsService.putApiV1WorkContracts(id, request),
    onSuccess: async (data, variables) => {
      // Await the list refetch: the history hands the row's updated_at to the
      // next edit as its optimistic-lock token, so the mutation must not
      // settle while the list still shows the pre-update value.
      await queryClient.invalidateQueries({
        queryKey: workforceKeys.contractList(variables.employeeId),
      });
      // The refetch can fail and leave the old row, and with it the old
      // token, in the cache. The PUT returned the row, so copy it in: the
      // next edit then carries the returned updated_at either way.
      queryClient.setQueryData<workforce_WorkContractListResponse>(
        workforceKeys.contractList(variables.employeeId),
        (current) => {
          if (!current?.work_contracts || !data.id) return current;
          return {
            ...current,
            work_contracts: current.work_contracts.map((contract) =>
              contract.id === data.id ? { ...contract, ...data } : contract
            ),
          };
        }
      );
      queryClient.invalidateQueries({
        queryKey: workforceKeys.summary(),
      });
      queryClient.setQueryData(
        workforceKeys.contractDetail(variables.id),
        data
      );
      invalidateEmployeeStatusDerivation(queryClient, variables.employeeId);
      // The vacation entitlement is derived from the contract's per-year values.
      queryClient.invalidateQueries({
        queryKey: workforceKeys.vacationSummaries(variables.employeeId),
      });
      toast.success(
        i18n._({
          id: 'workforce.toast.contractUpdateSuccess',
          message: 'Work contract updated successfully',
        })
      );
    },
    onError: (error: Error) =>
      handleWorkforceError(error, i18n, 'contractUpdate'),
  });
}

/**
 * Hook to delete a work contract.
 */
export function useDeleteWorkContract() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
    }: {
      id: string;
      employeeId: string;
    }) => WorkContractsService.deleteApiV1WorkContracts(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workforceKeys.contractList(variables.employeeId),
      });
      queryClient.invalidateQueries({
        queryKey: workforceKeys.summary(),
      });
      queryClient.removeQueries({
        queryKey: workforceKeys.contractDetail(variables.id),
      });
      invalidateEmployeeStatusDerivation(queryClient, variables.employeeId);
      toast.success(
        i18n._({
          id: 'workforce.toast.contractDeleteSuccess',
          message: 'Work contract deleted successfully',
        })
      );
    },
    onError: (error: Error) =>
      handleWorkforceError(error, i18n, 'contractDelete'),
  });
}

/**
 * Hook to adjust overtime for an employee.
 */
export function useAdjustOvertime() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<workforce_OvertimeAccountResponse, Error, AdjustOvertimeRequest>({
    mutationFn: (request) =>
      WorkforceService.postApiV1WorkforceOvertimeAdjust(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workforceKeys.overtime(),
      });
      queryClient.invalidateQueries({
        queryKey: workforceKeys.summary(),
      });
      toast.success(
        i18n._({
          id: 'workforce.toast.overtimeAdjustSuccess',
          message: 'Overtime adjusted successfully',
        })
      );
    },
    onError: (error: Error) =>
      handleWorkforceError(error, i18n, 'overtimeAdjust'),
  });
}

/**
 * Hook to create or update a vacation balance.
 */
export function useCreateVacationBalance() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation<workforce_VacationBalanceResponse, Error, CreateVacationBalanceRequest>({
    mutationFn: (request) =>
      VacationBalancesService.postApiV1VacationBalances(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workforceKeys.vacationBalance(
          variables.employee_id,
          variables.year
        ),
      });
      queryClient.invalidateQueries({
        queryKey: workforceKeys.summary(),
      });
      toast.success(
        i18n._({
          id: 'workforce.toast.vacationBalanceCreateSuccess',
          message: 'Vacation balance saved successfully',
        })
      );
    },
    onError: (error: Error) =>
      handleWorkforceError(error, i18n, 'vacationBalanceCreate'),
  });
}
